/**
 * Extrato de conversoes: uma linha por pagamento creditado a um toque do
 * motor (ADR-001), pronto para milhares de linhas.
 *
 * Decisoes nao obvias:
 *  1. Filtros COMBINAVEIS com contagem (facetas do servidor): "Fechado por"
 *     em pastilhas, por ser a pergunta do dono (E5); canal creditado, status,
 *     estorno e faixa de valor em seletores compactos; busca por lead,
 *     oferta, vendedora ou referencia do pedido. A contagem de cada opcao
 *     aplica todos os OUTROS filtros: e o numero que aparece se clicar.
 *  2. "Contato creditado" e "Fechado por" sao colunas separadas. A primeira e
 *     sempre um toque do motor (quem recebe o credito); a segunda so tem nome
 *     quando a vendedora fechou, com a entrada (webhook ou venda assistida).
 *     Nenhuma das duas substitui a outra.
 *  3. Estorno aparece NA LINHA mesmo quando a comissao foi mantida (reembolso
 *     nao reverte): "Reembolso em 12/09 · comissão mantida". So o chargeback
 *     (ou a politica da empresa) risca a comissao e muda o status.
 *  4. Ordenacao por coluna com `aria-sort` e botao no cabecalho (teclado);
 *     cada cabecalho explica a coluna em termos de negocio no `title`.
 *  5. Nove colunas nao cabem em 360px: abaixo de `md` vira lista de cartoes
 *     com as mesmas medidas; acima, rola em `overflow-x-auto`.
 *  6. Paginacao no servidor com tamanho escolhivel (20/50/100) e o rodape
 *     soma o RECORTE FILTRADO inteiro, nao a pagina.
 *  7. A busca espera 300 ms depois da ultima tecla: cada tecla nao pode
 *     virar uma consulta ao servidor.
 */

import { useEffect, useId, useState, type ReactNode } from 'react';
import { EmptyState, ErrorPanel } from '../../../components/PageState';
import { Pagination } from '../../../components/Pagination';
import { Badge, Button, FilterChips, Footnote, Icon, Input, SectionCard, Select, type ChipOption } from '../../../components/ui';
import type { AsyncState } from '../../../lib/api';
import { formatBRLFromCents, formatCount, formatDateTime, formatPercentFromBps, shortenName } from '../../../lib/format';
import type { CompanySummary } from '../../../types';
import {
  COLUNAS_DO_EXTRATO,
  contarFiltrosAtivos,
  ORDEM_DAS_COLUNAS,
  TAMANHOS_DE_PAGINA,
  type EstadoDaExportacao,
  type EstadoDoExtrato,
} from '../extrato';
import { ORDEM_FAIXAS } from '../regras';
import type {
  AmountBand,
  ChannelFilter,
  ClosedByFilter,
  ConversionFacets,
  ConversionRow,
  ConversionsResponse,
  CreditedContact,
  RefundFilter,
  SortColumn,
  StatusFilter,
} from '../types';
import {
  CANAL_CONTATO,
  ENTRADA,
  ESTORNO,
  FILTRO_CANAL,
  FILTRO_ESTORNO,
  FILTRO_FAIXA,
  FILTRO_FECHADO_POR,
  FILTRO_STATUS,
  ORDEM_CANAIS,
  ORDEM_FECHADO_POR,
  ORDEM_STATUS,
  ORIGEM_PAGAMENTO,
  POLITICA_ESTORNO,
  STATUS_CONVERSAO,
  formatOffset,
  plural,
  rotuloDoToque,
} from '../vocabulario';
import { IdentidadeEmpresa, identidadeDaEmpresa } from './IdentidadeEmpresa';

const ESPERA_DA_BUSCA_MS = 300;
const OPCOES_DE_ESTORNO: readonly RefundFilter[] = ['todos', 'sem_estorno', 'com_estorno', 'mantida', 'revertida'];

// -----------------------------------------------------------------------------
// Celulas
// -----------------------------------------------------------------------------

function Comissao({ row }: { readonly row: ConversionRow }): JSX.Element {
  const revertida = row.status === 'revertida';
  return (
    <span className={`tabular-nums ${revertida ? 'text-ink-subtle line-through' : 'font-medium text-ink'}`}>
      {formatBRLFromCents(row.commissionCents)}
      {revertida ? <span className="sr-only"> (revertida)</span> : null}
    </span>
  );
}

function ContatoCreditado({ contato }: { readonly contato: CreditedContact }): JSX.Element {
  const canal = CANAL_CONTATO[contato.channel];
  return (
    <span className="flex flex-col items-start gap-1" title={`Último toque do motor antes do pagamento: ${rotuloDoToque(contato)}. É ele que recebe o crédito da venda (ADR-001).`}>
      <Badge tone={canal.tone} icon={canal.icon}>
        {canal.label}
      </Badge>
      <span className="text-xs text-ink-subtle">
        <span className="font-medium tabular-nums text-ink-muted">{formatOffset(contato.offsetMinutes)}</span> · {formatDateTime(contato.at)}
      </span>
    </span>
  );
}

function FechadoPor({ row }: { readonly row: ConversionRow }): JSX.Element {
  const v = row.closedBy;
  if (v === null) {
    return <span className="text-xs text-ink-subtle">Direto no checkout</span>;
  }
  const entrada = ENTRADA[v.via];
  return (
    <span
      className="flex flex-col items-start gap-1"
      title={`${shortenName(v.sellerName)} fechou depois que o lead respondeu ao toque ${rotuloDoToque(row.creditedContact)}. A comissão continua creditada ao toque (ADR-001 E5). ${entrada.hint}`}
    >
      <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
        <Icon name="usuarios" size="xs" className="text-brand" />
        {shortenName(v.sellerName)}
      </span>
      <Badge tone={entrada.tone} icon={entrada.icon}>
        {entrada.label}
      </Badge>
      {v.orderRef === null ? null : <span className="max-w-[12rem] truncate text-[11px] text-ink-subtle">Pedido {v.orderRef}</span>}
      {v.registeredAt === null ? null : <span className="text-[11px] text-ink-subtle">Registrada em {formatDateTime(v.registeredAt)}</span>}
    </span>
  );
}

function StatusDaConversao({ row }: { readonly row: ConversionRow }): JSX.Element {
  const info = STATUS_CONVERSAO[row.status];
  const e = row.refund;
  return (
    <span className="flex flex-col items-start gap-1">
      <Badge tone={info.tone} dot>
        {info.label}
      </Badge>
      {e === null ? null : (
        <span className={`text-xs ${e.reversed ? 'text-status-critical' : 'text-ink-subtle'}`} title={`Política aplicada: ${POLITICA_ESTORNO[e.policyApplied].label}`}>
          {ESTORNO[e.kind].label} de {formatBRLFromCents(e.amountCents)} em {formatDateTime(e.at)} · comissão{' '}
          <strong className="font-semibold">{e.reversed ? 'revertida' : 'mantida'}</strong>
        </span>
      )}
    </span>
  );
}

function LeadEOferta({ row }: { readonly row: ConversionRow }): JSX.Element {
  return (
    <span className="block min-w-0">
      <span className="block text-ink">{shortenName(row.leadName)}</span>
      <span className="block truncate text-xs text-ink-muted">{row.offerName}</span>
      <span className="block text-[11px] text-ink-subtle">{ORIGEM_PAGAMENTO[row.source]}</span>
    </span>
  );
}

// -----------------------------------------------------------------------------
// Filtros
// -----------------------------------------------------------------------------

function SeletorComContagem<T extends string>({
  rotulo,
  valor,
  opcoes,
  rotulos,
  contagem,
  onChange,
}: {
  readonly rotulo: string;
  readonly valor: T;
  readonly opcoes: readonly T[];
  readonly rotulos: Readonly<Record<T, string>>;
  readonly contagem: Readonly<Record<T, number>> | null;
  readonly onChange: (valor: T) => void;
}): JSX.Element {
  const id = useId();
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={id} className="text-[11px] font-medium text-ink-subtle">
        {rotulo}
      </label>
      <Select
        id={id}
        size="sm"
        value={valor}
        onChange={(event) => {
          const escolhido = opcoes.find((o) => o === event.target.value);
          if (escolhido !== undefined) {
            onChange(escolhido);
          }
        }}
      >
        {opcoes.map((o) => (
          <option key={o} value={o}>
            {rotulos[o]}
            {contagem === null ? '' : ` (${formatCount(contagem[o])})`}
          </option>
        ))}
      </Select>
    </div>
  );
}

function PastilhaAtiva({ children, onRemover, rotulo }: { readonly children: ReactNode; readonly onRemover: () => void; readonly rotulo: string }): JSX.Element {
  return (
    <button
      type="button"
      onClick={onRemover}
      aria-label={`Remover filtro: ${rotulo}`}
      className="inline-flex h-7 items-center gap-1.5 rounded-full border border-brand/40 bg-brand/10 px-2.5 text-[11px] font-semibold text-brand transition-colors hover:bg-brand/20"
    >
      {children}
      <Icon name="x" size="xs" />
    </button>
  );
}

// -----------------------------------------------------------------------------
// Cabecalho ordenavel
// -----------------------------------------------------------------------------

function Cabecalho({ coluna, estado, onOrdenar }: { readonly coluna: SortColumn; readonly estado: EstadoDoExtrato; readonly onOrdenar: (coluna: SortColumn) => void }): JSX.Element {
  const info = COLUNAS_DO_EXTRATO[coluna];
  const ativa = estado.sort === coluna;
  return (
    <th scope="col" aria-sort={ativa ? (estado.desc ? 'descending' : 'ascending') : 'none'} className={`px-3 py-3 font-medium first:pl-5 last:pr-5 ${info.numerica ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={() => onOrdenar(coluna)}
        title={info.titulo}
        className={`inline-flex items-center gap-1 rounded-md uppercase tracking-wide transition-colors hover:text-ink ${ativa ? 'text-ink' : ''} ${info.numerica ? 'flex-row-reverse' : ''}`}
      >
        {info.rotulo}
        <span aria-hidden="true" className={`text-[10px] ${ativa ? '' : 'opacity-40'}`}>
          {ativa ? (estado.desc ? '▼' : '▲') : '↕'}
        </span>
      </button>
    </th>
  );
}

// -----------------------------------------------------------------------------
// Extrato
// -----------------------------------------------------------------------------

export function ExtratoDeConversoes({
  state,
  onRetry,
  estado,
  onEstado,
  onOrdenar,
  onLimpar,
  companies,
  empresaFiltrada,
  onTirarEmpresa,
  exportacao,
  onExportar,
}: {
  readonly state: AsyncState<ConversionsResponse>;
  readonly onRetry: () => void;
  readonly estado: EstadoDoExtrato;
  /** Troca filtros ou pagina. Filtro novo volta para a primeira pagina (quem decide e a pagina). */
  readonly onEstado: (patch: Partial<EstadoDoExtrato>) => void;
  readonly onOrdenar: (coluna: SortColumn) => void;
  readonly onLimpar: () => void;
  readonly companies: readonly CompanySummary[];
  readonly empresaFiltrada: CompanySummary | null;
  readonly onTirarEmpresa: () => void;
  readonly exportacao: EstadoDaExportacao;
  readonly onExportar: () => void;
}): JSX.Element {
  const idBusca = useId();
  const [busca, setBusca] = useState(estado.search);

  // Busca externa (limpar filtros, insight) sobrescreve o que esta digitado.
  useEffect(() => {
    setBusca(estado.search);
  }, [estado.search]);

  useEffect(() => {
    if (busca === estado.search) {
      return undefined;
    }
    const timer = window.setTimeout(() => onEstado({ search: busca }), ESPERA_DA_BUSCA_MS);
    return () => window.clearTimeout(timer);
  }, [busca, estado.search, onEstado]);

  const dados = state.status === 'success' ? state.data : null;
  const facetas: ConversionFacets | null = dados === null ? null : dados.facets;
  const total = dados === null ? 0 : dados.page.total;
  const ativos = contarFiltrosAtivos(estado);
  const atualizando = state.status === 'success' && state.refreshing;

  const chipsFechadoPor: readonly ChipOption<ClosedByFilter>[] = ORDEM_FECHADO_POR.map((f) => ({
    value: f,
    label: FILTRO_FECHADO_POR[f].label,
    count: facetas === null ? undefined : formatCount(facetas.closedBy[f]),
  }));

  const textoDoBotao =
    exportacao.status === 'gerando' ? 'Gerando CSV…' : total > 0 ? `Exportar CSV · ${formatCount(total)} ${plural(total, 'linha', 'linhas')}` : 'Exportar CSV';

  return (
    <SectionCard
      id="extrato"
      title="Extrato de conversões"
      icon="comissao"
      className="scroll-mt-20"
      description="Uma linha por venda paga até 7 dias depois de um toque do motor, com percentual e comissão congelados. “Fechado por” mostra a vendedora quando o comercial fechou; o crédito continua do toque."
      padded={false}
      aside={
        <>
          <Button variant="secondary" size="sm" icon="download" onClick={onExportar} disabled={total === 0} loading={exportacao.status === 'gerando'}>
            {textoDoBotao}
          </Button>
          <span role="status" className="text-xs">
            {exportacao.status === 'pronto' ? (
              <Badge tone={exportacao.truncado ? 'warning' : 'good'} icon="check">
                {exportacao.truncado ? `CSV com as primeiras ${formatCount(exportacao.linhas)} de ${formatCount(exportacao.total)}` : `CSV gerado · ${formatCount(exportacao.linhas)} linhas`}
              </Badge>
            ) : exportacao.status === 'erro' ? (
              <Badge tone="critical" icon="alerta">
                {exportacao.mensagem}
              </Badge>
            ) : null}
          </span>
        </>
      }
    >
      <div className="flex flex-col gap-4 px-5 pb-4">
        <div className="overflow-x-auto pb-1">
          <FilterChips label="Filtrar por quem fechou a venda" prefix="Fechado por:" options={chipsFechadoPor} value={estado.closedBy} onChange={(closedBy) => onEstado({ closedBy })} />
        </div>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <SeletorComContagem<ChannelFilter>
            rotulo="Contato creditado"
            valor={estado.channel}
            opcoes={['todos', ...ORDEM_CANAIS]}
            rotulos={FILTRO_CANAL}
            contagem={facetas === null ? null : facetas.channel}
            onChange={(channel) => onEstado({ channel })}
          />
          <SeletorComContagem<StatusFilter>
            rotulo="Status da comissão"
            valor={estado.status}
            opcoes={['todos', ...ORDEM_STATUS]}
            rotulos={FILTRO_STATUS}
            contagem={facetas === null ? null : facetas.status}
            onChange={(status) => onEstado({ status })}
          />
          <SeletorComContagem<RefundFilter>
            rotulo="Estorno"
            valor={estado.refund}
            opcoes={OPCOES_DE_ESTORNO}
            rotulos={FILTRO_ESTORNO}
            contagem={facetas === null ? null : facetas.refund}
            onChange={(refund) => onEstado({ refund })}
          />
          <SeletorComContagem<AmountBand>
            rotulo="Valor pago"
            valor={estado.amount}
            opcoes={['todas', ...ORDEM_FAIXAS]}
            rotulos={FILTRO_FAIXA}
            contagem={facetas === null ? null : facetas.amount}
            onChange={(amount) => onEstado({ amount })}
          />
          <div className="flex min-w-0 flex-col gap-1">
            <label htmlFor={idBusca} className="text-[11px] font-medium text-ink-subtle">
              Buscar
            </label>
            <div className="relative">
              <Icon name="busca" size="sm" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink-subtle" />
              <Input
                id={idBusca}
                size="sm"
                type="search"
                placeholder="Lead, oferta, vendedora, pedido"
                value={busca}
                onChange={(event) => setBusca(event.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </div>

        {ativos === 0 && empresaFiltrada === null ? null : (
          <div role="group" className="flex flex-wrap items-center gap-2" aria-label="Filtros ativos">
            <span className="text-[11px] font-medium text-ink-subtle">Filtros ativos:</span>
            {empresaFiltrada === null ? null : (
              <PastilhaAtiva rotulo={`Empresa ${empresaFiltrada.name}`} onRemover={onTirarEmpresa}>
                Empresa: {empresaFiltrada.name}
              </PastilhaAtiva>
            )}
            {estado.closedBy === 'todos' ? null : (
              <PastilhaAtiva rotulo={FILTRO_FECHADO_POR[estado.closedBy].label} onRemover={() => onEstado({ closedBy: 'todos' })}>
                Fechado por: {FILTRO_FECHADO_POR[estado.closedBy].label}
              </PastilhaAtiva>
            )}
            {estado.channel === 'todos' ? null : (
              <PastilhaAtiva rotulo={FILTRO_CANAL[estado.channel]} onRemover={() => onEstado({ channel: 'todos' })}>
                Toque: {FILTRO_CANAL[estado.channel]}
              </PastilhaAtiva>
            )}
            {estado.status === 'todos' ? null : (
              <PastilhaAtiva rotulo={FILTRO_STATUS[estado.status]} onRemover={() => onEstado({ status: 'todos' })}>
                Status: {FILTRO_STATUS[estado.status]}
              </PastilhaAtiva>
            )}
            {estado.refund === 'todos' ? null : (
              <PastilhaAtiva rotulo={FILTRO_ESTORNO[estado.refund]} onRemover={() => onEstado({ refund: 'todos' })}>
                {FILTRO_ESTORNO[estado.refund]}
              </PastilhaAtiva>
            )}
            {estado.amount === 'todas' ? null : (
              <PastilhaAtiva rotulo={FILTRO_FAIXA[estado.amount]} onRemover={() => onEstado({ amount: 'todas' })}>
                {FILTRO_FAIXA[estado.amount]}
              </PastilhaAtiva>
            )}
            {estado.search.trim() === '' ? null : (
              <PastilhaAtiva rotulo={`busca ${estado.search}`} onRemover={() => onEstado({ search: '' })}>
                “{estado.search.trim()}”
              </PastilhaAtiva>
            )}
            {ativos > 0 ? (
              <Button variant="ghost" size="sm" icon="x" onClick={onLimpar}>
                Limpar {formatCount(ativos)} {plural(ativos, 'filtro', 'filtros')}
              </Button>
            ) : null}
          </div>
        )}
      </div>

      {state.status === 'loading' ? (
        <>
          <p role="status" className="sr-only">
            Carregando o extrato
          </p>
          <div aria-hidden="true" className="flex flex-col gap-2 px-5 pb-5">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-14 animate-pulse rounded-lg bg-ink/5" />
            ))}
          </div>
        </>
      ) : null}

      {state.status === 'error' ? (
        <div className="px-5 pb-5">
          <ErrorPanel title="Não foi possível carregar o extrato" error={state.error} onRetry={onRetry} />
        </div>
      ) : null}

      {dados === null ? null : dados.facets.closedBy.todos === 0 && ativos === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState
            title="Nenhuma conversão no período"
            description="Quando um lead pagar até 7 dias depois de um toque do motor, a venda entra aqui com a comissão congelada, e com a vendedora em “Fechado por” se o comercial fechou. Troque a empresa ou a janela do cabeçalho para ver outro recorte."
          />
        </div>
      ) : total === 0 ? (
        <div className="flex flex-col items-start gap-3 px-5 pb-5">
          <p className="text-sm text-ink-muted">
            Nenhuma conversão combina com {plural(ativos, 'este filtro', 'estes filtros')}. As contagens ao lado de cada opção mostram quantas aparecem se você trocar
            aquele filtro.
          </p>
          <Button variant="secondary" size="sm" icon="x" onClick={onLimpar}>
            Limpar filtros
          </Button>
        </div>
      ) : (
        <div className={`transition-opacity ${atualizando ? 'opacity-70' : ''}`} aria-busy={atualizando}>
          {/* Desktop */}
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[82rem] border-collapse text-sm">
              <caption className="sr-only">
                Extrato de conversões com pagamento, empresa, lead e oferta, valor pago, percentual aplicado, comissão, contato creditado, quem fechou e status.
                Ordenável pelos botões do cabeçalho.
              </caption>
              <thead>
                <tr className="border-y border-line text-left text-xs uppercase tracking-wide text-ink-subtle">
                  {ORDEM_DAS_COLUNAS.slice(0, 2).map((coluna) => (
                    <Cabecalho key={coluna} coluna={coluna} estado={estado} onOrdenar={onOrdenar} />
                  ))}
                  <th scope="col" className="px-3 py-3 font-medium" title="Lead (abreviado), oferta e de onde veio o pagamento">
                    Lead e oferta
                  </th>
                  {ORDEM_DAS_COLUNAS.slice(2).map((coluna) => (
                    <Cabecalho key={coluna} coluna={coluna} estado={estado} onOrdenar={onOrdenar} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {dados.page.items.map((row) => (
                  <tr key={row.id} className="border-b border-line/60 align-top transition-colors last:border-0 hover:bg-ink/5">
                    <th scope="row" className="px-5 py-3 text-left font-normal">
                      <span className="block tabular-nums text-ink">{formatDateTime(row.paidAt)}</span>
                    </th>
                    <td className="max-w-[12rem] px-3 py-3">
                      <IdentidadeEmpresa empresa={identidadeDaEmpresa(row.companyId, companies, row.companyName)} size="sm" />
                    </td>
                    <td className="max-w-[14rem] px-3 py-3">
                      <LeadEOferta row={row} />
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums text-ink">{formatBRLFromCents(row.paidAmountCents)}</td>
                    <td className="px-3 py-3 text-right tabular-nums text-ink-muted">{formatPercentFromBps(row.feeBpsApplied)}</td>
                    <td className="px-3 py-3 text-right">
                      <Comissao row={row} />
                    </td>
                    <td className="px-3 py-3">
                      <ContatoCreditado contato={row.creditedContact} />
                    </td>
                    <td className="px-3 py-3">
                      <FechadoPor row={row} />
                    </td>
                    <td className="max-w-[16rem] px-3 py-3 pr-5">
                      <StatusDaConversao row={row} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile */}
          <ul className="flex flex-col gap-3 px-4 pb-2 md:hidden">
            {dados.page.items.map((row) => (
              <li key={row.id} className="flex flex-col gap-3 rounded-xl border border-line bg-surface px-4 py-4">
                <div className="flex items-start justify-between gap-3">
                  <IdentidadeEmpresa empresa={identidadeDaEmpresa(row.companyId, companies, row.companyName)} size="md" />
                  <span className="text-right">
                    <span className="block">
                      <Comissao row={row} />
                    </span>
                    <span className="block text-[11px] text-ink-subtle">
                      {formatPercentFromBps(row.feeBpsApplied)} de {formatBRLFromCents(row.paidAmountCents)}
                    </span>
                  </span>
                </div>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div className="col-span-2">
                    <dt className="text-xs text-ink-subtle">Lead e oferta</dt>
                    <dd>
                      <LeadEOferta row={row} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-subtle">Pagamento</dt>
                    <dd className="tabular-nums text-ink">{formatDateTime(row.paidAt)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-subtle">Status</dt>
                    <dd className="mt-0.5">
                      <StatusDaConversao row={row} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-subtle">Contato creditado</dt>
                    <dd className="mt-1">
                      <ContatoCreditado contato={row.creditedContact} />
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-ink-subtle">Fechado por</dt>
                    <dd className="mt-1">
                      <FechadoPor row={row} />
                    </dd>
                  </div>
                </dl>
              </li>
            ))}
          </ul>

          <div className="flex flex-col gap-3 border-t border-line px-5 pb-5 pt-3">
            <p className="text-xs text-ink-muted">
              No filtro: <strong className="font-semibold tabular-nums text-ink">{formatCount(dados.totals.count)}</strong>{' '}
              {plural(dados.totals.count, 'conversão', 'conversões')} · {formatBRLFromCents(dados.totals.paidAmountCents)} pagos · comissão ativa{' '}
              <strong className="font-semibold tabular-nums text-ink">{formatBRLFromCents(dados.totals.activeCommissionCents)}</strong>
              {dados.totals.reversedCommissionCents > 0 ? <> · {formatBRLFromCents(dados.totals.reversedCommissionCents)} revertidos</> : null}
            </p>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <label className="flex items-center gap-2 text-xs text-ink-muted">
                Linhas por página
                <Select
                  size="sm"
                  value={String(estado.limit)}
                  onChange={(event) => {
                    const n = TAMANHOS_DE_PAGINA.find((t) => String(t) === event.target.value);
                    if (n !== undefined) {
                      onEstado({ limit: n, offset: 0 });
                    }
                  }}
                  className="w-20"
                >
                  {TAMANHOS_DE_PAGINA.map((t) => (
                    <option key={t} value={String(t)}>
                      {t}
                    </option>
                  ))}
                </Select>
              </label>
              <div className="min-w-[16rem] flex-1">
                <Pagination total={total} limit={dados.page.limit} offset={dados.page.offset} onChange={(offset) => onEstado({ offset })} />
              </div>
            </div>
            <Footnote>
              O CSV traz o recorte filtrado inteiro (todas as páginas), com “Contato creditado” e “Fechado por” em colunas separadas e o lead abreviado como na
              tela.
            </Footnote>
          </div>
        </div>
      )}
    </SectionCard>
  );
}
